import { useCallback } from 'react';

import retry from 'async-retry';

import { useCheckOrderExistsOnDemand, useCreateOrderOnDemand } from '@api';
import { EAdyenComponentState, EOrderState } from '@model';
import { ELogLevel, useErrorContext } from '@services/errorService';
import { useRedirect } from '@services/routingService';
import { EPage } from '@types';

import {
  ORDER_CHECK_EXISTS_RETRIES,
  ORDER_CHECK_EXISTS_RETRY_FACTOR,
  ORDER_CHECK_EXISTS_TIMEOUT,
} from '../../constants';

export const useHandleOrderCreation = () => {
  const { request: creatOrder } = useCreateOrderOnDemand();
  const { request: checkOrderExists } = useCheckOrderExistsOnDemand();
  const redirect = useRedirect();
  const { callLogger } = useErrorContext();

  const handleOrderCreation = useCallback(
    async (orderNumber: number, onClose: () => void, setComponentState: (state: EAdyenComponentState) => void) => {
      try {
        setComponentState(EAdyenComponentState.ORDER_CREATE_IN_PROGRESS);

        const result = await creatOrder(orderNumber);
        if (result?.orderState === EOrderState.Complete) {
          onClose();
          redirect(EPage.THANK_YOU);
          return;
        }
      } catch (error) {
        callLogger({
          logLevel: ELogLevel.ERROR,
          logError: Error(`Order creation failed for oalt with orderId: (${orderNumber}).`),
        });
      }

      try {
        const orderFound = await retry(
          async () => {
            const res = await checkOrderExists({ orderId: orderNumber });

            if (!res?.orderExists) {
              throw new Error('Order does not exist yet');
            }

            return res.orderExists;
          },
          {
            retries: ORDER_CHECK_EXISTS_RETRIES,
            minTimeout: ORDER_CHECK_EXISTS_TIMEOUT,
            maxTimeout: ORDER_CHECK_EXISTS_TIMEOUT,
            factor: ORDER_CHECK_EXISTS_RETRY_FACTOR,
          },
        );

        if (orderFound) {
          onClose();
          redirect(EPage.THANK_YOU);
          return;
        }
      } catch (e) {
        setComponentState(EAdyenComponentState.ORDER_CREATE_FAIL);
        callLogger({
          logLevel: ELogLevel.ERROR,
          logError: Error(`Order still not created even from Adyen webhook with orderId: (${orderNumber}).`),
        });
      }
    },
    [callLogger, checkOrderExists, creatOrder, redirect],
  );

  return { handleOrderCreation };
};
